import {
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	isRouteErrorResponse,
	data,
	useLoaderData,
} from 'react-router'
import { ToastProvider, ErrorState, Button } from '@jpg/design-system'
import { NavigationFeedback } from '#app/components/NavigationFeedback'
import { ClientHintCheck, getHints } from '#app/utils/client-hints'
import { getEnv } from '#app/utils/env.server'
import { getDomainUrl } from '@jpg/react-router-utils'
import { useNonce } from '#app/utils/nonce-provider'
import type { Route } from './+types/root'
import './styles/tailwind.css'

export const links: Route.LinksFunction = () => {
	return [
		{ rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
		{ rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
		{ rel: 'icon', type: 'image/png', sizes: '256x256', href: '/favicon.png' },
		{ rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
		{ rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }: { data: any }) => {
	return [
		{ title: data ? 'JPG Senior Pilgrimage: Quo Vadis' : 'Error | JPG Senior Pilgrimage: Quo Vadis' },
		{ name: 'description', content: 'JPG Senior Pilgrimage: Quo Vadis — JPG Academy' },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const env = getEnv()
	return data(
		{
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: 'light',
				},
			},
			ENV: env,
		},
		{
			headers: {
				'Server-Timing': `response;desc="Root loader"`,
			},
		},
	)
}

// Document errors reach the root boundary; retain the failed loader's retry
// and privacy headers instead of dropping them during framework rendering.
export const headers: Route.HeadersFunction = ({ loaderHeaders, errorHeaders }) => {
	const result = new Headers(loaderHeaders)
	errorHeaders?.forEach((value, key) => result.set(key, value))
	return result
}

export function Layout({ children }: { children: React.ReactNode }) {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	return (
		<html lang="en">
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<Links />
			</head>
			<body className="bg-cream text-navy-dark antialiased">
				<ToastProvider>
					{children}
				</ToastProvider>
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(data?.ENV ?? {})}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export default function App() {
	return <><NavigationFeedback /><Outlet /></>
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
	const is503 = isRouteErrorResponse(error) && error.status === 503
	const is404 = isRouteErrorResponse(error) && error.status === 404
	const code = String(
		is404 ? 404 : isRouteErrorResponse(error) ? error.status : 'Error',
	)
	const title = is503
		? 'Temporarily unavailable'
		: is404
			? 'Page Not Found'
			: isRouteErrorResponse(error)
				? error.statusText || 'Something went wrong'
				: 'Unexpected Error'
	const description = is503
		? 'We could not load this page right now. Please wait a moment, then try again.'
		: is404
			? "The page you're looking for doesn't exist or has been moved."
			: import.meta.env.DEV && error instanceof Error
				? error.message
				: 'An unexpected error occurred. Please try again or contact the administrator.'

	return (
		<div className="flex min-h-screen items-center justify-center p-4 sm:p-8">
			<ErrorState
				fullPage
				code={code}
				eyebrow={is503 ? 'Please try again' : is404 ? 'Page not found' : 'Application error'}
				title={title}
				description={description}
				primaryAction={is503 ? (
					<Button variant="primary" size="lg" onClick={() => window.location.reload()}>
						Try again
					</Button>
				) : (
					<Link to="/">
						<Button variant="primary" size="lg">
							Go to Dashboard
						</Button>
					</Link>
				)}
			/>
		</div>
	)
}
export { shouldRevalidate } from '#app/utils/presentation-navigation'
